// Phase 40 Plan 04 Task 4 — events sync cursor flush.
// SYNC-04 closes INT-02. CONTEXT specifies the cursor advance invariant:
// `Date.parse(ev.ts) > Number(cursor)` — handles the ISO-string ts the Phase 32
// producer emits (`{e, ts: '2026-...'}`) versus the epoch-ms cursor stored in
// localStorage as a string.
//
// Public exports:
//   flushEvents({ jwt }) — read tend:eventsSyncCursor + tend:events,
//                          POST events with Date.parse(ts) > Number(cursor)
//                          to /events/sync, advance cursor on 200, leave
//                          cursor untouched on non-200.
//   scheduleEventsFlush({ jwt }) — register the 60s interval (idempotent).
//   stopEventsFlush() — clear the interval (called from AuthProvider on
//                       SIGNED_OUT).

import { getApiBase } from './sync.js';

const CURSOR_KEY = 'tend:eventsSyncCursor';
const EVENTS_KEY = 'tend:events';
const DEFAULT_FLUSH_INTERVAL_MS = 60_000;

function readCursor() {
  try { return localStorage.getItem(CURSOR_KEY) || '0'; } catch { return '0'; }
}

function readEvents() {
  try {
    const raw = localStorage.getItem(EVENTS_KEY) || '[]';
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function writeCursor(value) {
  try { localStorage.setItem(CURSOR_KEY, String(value)); }
  catch { /* QuotaExceeded — leave cursor at last good value */ }
}

/**
 * Flush events accumulated in tend:events to the Worker /events/sync route.
 *
 * @param {object} args
 * @param {string} args.jwt — Supabase access token
 * @returns {Promise<{flushed:number, error?:string, skipped?:string}>}
 */
export async function flushEvents({ jwt } = {}) {
  if (!jwt) return { flushed: 0, skipped: 'no-jwt' };
  const cursor = readCursor();
  const events = readEvents();
  // SYNC-04 verbatim: Date.parse(ts) > Number(cursor). Future producers that
  // emit a numeric epoch-ms ts get NaN from Date.parse and are filtered out
  // here — that's intentional until the producer normalises to a parseable
  // shape (closes INT-02).
  const filtered = events.filter((ev) => {
    if (!ev || ev.ts == null) return false;
    const parsed = Date.parse(ev.ts);
    return Number.isFinite(parsed) && parsed > Number(cursor);
  });
  if (filtered.length === 0) return { flushed: 0 };
  try {
    const res = await fetch(`${getApiBase()}/events/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwt}`,
      },
      body: JSON.stringify({ events: filtered }),
    });
    if (!res.ok) return { flushed: 0, error: `HTTP ${res.status}` };
    // Advance cursor to the latest event's ts. The non-200 path leaves the
    // cursor untouched so the next flush retries the same window.
    const latestTs = Math.max(...filtered.map((ev) => Date.parse(ev.ts)));
    if (Number.isFinite(latestTs)) writeCursor(latestTs);
    return { flushed: filtered.length };
  } catch (e) {
    return { flushed: 0, error: e?.message || 'network-error' };
  }
}

let flushInterval = null;

/**
 * Register a 60-second interval that flushes events when online. Idempotent —
 * a second call while a timer exists is a no-op.
 *
 * @param {object} args
 * @param {string} args.jwt — Supabase access token
 * @param {number} [args.intervalMs] — override (test seam)
 */
export function scheduleEventsFlush({ jwt, intervalMs = DEFAULT_FLUSH_INTERVAL_MS } = {}) {
  if (flushInterval) return;
  const trigger = () => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    flushEvents({ jwt }).catch(() => {});
  };
  flushInterval = setInterval(trigger, intervalMs);
  trigger();
}

/** Clear the interval (called from AuthProvider on SIGNED_OUT). */
export function stopEventsFlush() {
  if (flushInterval) {
    clearInterval(flushInterval);
    flushInterval = null;
  }
}
